import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fort Worth Dental Google Review URL - Direct link to write a review
// Using g.page short URL format (official Google review link)
const GOOGLE_REVIEW_URL = "https://g.page/r/CUolB0PBcru6EBM/review";

const ChatWidget = () => {
  const { pathname } = useLocation();
  const isEs = pathname === '/es' || pathname.startsWith('/es/');
  const T = (en, es) => (isEs ? es : en);
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState('home'); // home, messages, help, feedback, appointment, question, forms
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [feedbackType, setFeedbackType] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferred_day: '',
    preferred_time: '',
    patient_type: 'new',
    rating: 0,
    topic: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start session when widget opens
  const startSession = useCallback(async () => {
    if (sessionId) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat/session/start`, null, {
        params: {
          source_page: window.location.pathname,
          client_ip: '',
          user_agent: navigator.userAgent
        }
      });
      setSessionId(response.data.session_id);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to start chat session:', error);
    }
  }, [sessionId]);

  useEffect(() => {
    if (isOpen && !sessionId) {
      startSession();
    }
  }, [isOpen, sessionId, startSession]);

  // Handle AI Chat message send
  const handleSendMessage = async () => {
    if (!chatInput.trim() || isAiTyping) return;
    
    const userMessageText = chatInput.trim();
    setChatInput('');
    setIsAiTyping(true);
    
    // Optimistically add user message
    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      session_id: sessionId,
      message_type: 'user',
      content: userMessageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat/ai-chat`, {
        session_id: sessionId,
        message: userMessageText
      });
      
      if (response.data.success) {
        // Replace temp message with actual and add AI response
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== tempUserMsg.id);
          return [
            ...filtered,
            response.data.user_message,
            response.data.ai_response
          ];
        });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Add error message
      setMessages(prev => [...prev, {
        id: 'error-' + Date.now(),
        message_type: 'bot',
        content: "Sorry, I'm having trouble connecting. Please call us at (817) 985-3337 for immediate assistance.",
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsAiTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSubmitFeedback = async () => {
    if (!formData.message.trim()) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/chat/feedback`, {
        session_id: sessionId,
        feedback_type: feedbackType,
        message: formData.message,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        rating: formData.rating || null
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        setCurrentView('home');
        setSubmitSuccess(false);
        setFeedbackType(null);
        setFormData({ ...formData, message: '', rating: 0 });
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitAppointment = async () => {
    if (!formData.name || !formData.phone || !formData.email) return;
    
    // Capture browser/device info for debugging
    const browserInfo = {
      user_agent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      is_mobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      is_touch: 'ontouchstart' in window || navigator.maxTouchPoints > 0
    };

    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/chat/appointment-request`, {
        session_id: sessionId,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        preferred_day: formData.preferred_day,
        preferred_time: formData.preferred_time,
        patient_type: formData.patient_type,
        message: formData.message,
        browser_info: browserInfo
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        setCurrentView('home');
        setSubmitSuccess(false);
        setFormData({ name: '', email: '', phone: '', message: '', preferred_day: '', preferred_time: '', patient_type: 'new', rating: 0, topic: '' });
      }, 2000);
    } catch (error) {
      console.error('Failed to submit appointment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitQuestion = async () => {
    if (!formData.message.trim()) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/chat/question`, {
        session_id: sessionId,
        question: formData.message,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        topic: formData.topic
      });
      setSubmitSuccess(true);
      setTimeout(() => {
        setCurrentView('home');
        setSubmitSuccess(false);
        setFormData({ ...formData, message: '', topic: '' });
      }, 2000);
    } catch (error) {
      console.error('Failed to submit question:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setIsOpen(false);
    setCurrentView('home');
    setFeedbackType(null);
    setSubmitSuccess(false);
  };

  // Render the floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-700 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform z-50"
        aria-label="Open chat"
      >
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-400 via-teal-500 to-teal-600 p-6 text-white relative">
        <button
          onClick={resetAndClose}
          className="absolute top-4 right-4 text-white/80 hover:text-white"
          aria-label="Close chat"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        {/* Logo */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
            <span className="text-teal-600 font-bold text-xl">FWD</span>
          </div>
        </div>
        
        {currentView === 'home' && (
          <>
            <h2 className="text-2xl font-light">{T('Hello', 'Hola')}</h2>
            <p className="text-xl font-semibold">{T('How can we help?', '¿Cómo podemos ayudarte?')}</p>
          </>
        )}
        {currentView === 'feedback' && (
          <h2 className="text-xl font-semibold">Customer Feedback</h2>
        )}
        {currentView === 'appointment' && (
          <h2 className="text-xl font-semibold">Schedule Appointment</h2>
        )}
        {currentView === 'help' && (
          <h2 className="text-xl font-semibold">Help Center</h2>
        )}
        {currentView === 'forms' && (
          <h2 className="text-xl font-semibold">Patient Forms</h2>
        )}
        {currentView === 'question' && (
          <h2 className="text-xl font-semibold">Ask a Question</h2>
        )}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Home View */}
        {currentView === 'home' && (
          <div className="space-y-3">
            {/* Schedule Appointment */}
            <button
              onClick={() => setCurrentView('appointment')}
              className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition text-left"
            >
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800">SCHEDULE APPOINTMENT</h3>
                <p className="text-sm text-gray-500">Accepting new patients</p>
              </div>
            </button>

            {/* Write a Review */}
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                window.open(GOOGLE_REVIEW_URL, '_blank', 'noopener,noreferrer');
              }}
              className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition block"
            >
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800">WRITE US A REVIEW</h3>
                <p className="text-sm text-gray-500">Your 5-star reviews mean the world to us</p>
              </div>
            </a>

            {/* Send Feedback */}
            <button
              onClick={() => setCurrentView('feedback')}
              className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition text-left"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800">SEND US FEEDBACK</h3>
                <p className="text-sm text-gray-500">How are we doing?</p>
              </div>
            </button>
          </div>
        )}

        {/* Feedback View */}
        {currentView === 'feedback' && !feedbackType && (
          <div className="space-y-4">
            <div className="bg-gray-100 rounded-xl p-4">
              <p className="text-gray-700">
                Hey there! Welcome to Fort Worth Dental! We're all about providing the best service possible and making sure you have a great experience. If there's anything that you're happy with or think we can do even better, we would absolutely love to hear from you!
              </p>
            </div>
            
            <div className="flex items-center gap-3 text-gray-600">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-lg">💬</span>
              </div>
              <p className="font-medium">What is the nature of your feedback?</p>
            </div>

            <div className="space-y-2">
              {[
                { key: 'praise', label: 'Praise', letter: 'A' },
                { key: 'suggestions', label: 'Suggestions', letter: 'B' },
                { key: 'comments', label: 'Comments', letter: 'C' },
                { key: 'questions', label: 'Questions', letter: 'D' }
              ].map((option) => (
                <button
                  key={option.key}
                  onClick={() => setFeedbackType(option.key)}
                  className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:border-teal-500 hover:bg-teal-50 transition text-left"
                >
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 font-bold text-sm">
                    {option.letter}
                  </div>
                  <span className="font-medium text-gray-800">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Feedback Form */}
        {currentView === 'feedback' && feedbackType && !submitSuccess && (
          <div className="space-y-4">
            <button
              onClick={() => setFeedbackType(null)}
              className="text-teal-600 text-sm flex items-center gap-1"
            >
              ← Back
            </button>
            
            <h3 className="font-bold text-gray-800 capitalize">{feedbackType}</h3>
            
            {feedbackType === 'praise' && (
              <div className="flex justify-center gap-2 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className={`text-3xl ${formData.rating >= star ? 'text-yellow-400' : 'text-gray-300'}`}
                  >
                    ★
                  </button>
                ))}
              </div>
            )}
            
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder={`Share your ${feedbackType}...`}
              className="w-full border border-gray-200 rounded-xl p-3 h-32 resize-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            
            <div className="space-y-3">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name (optional)"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email (optional)"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Phone (optional)"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
              />
            </div>
            
            <button
              onClick={handleSubmitFeedback}
              disabled={isSubmitting || !formData.message.trim()}
              className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 disabled:bg-gray-300 transition"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        )}

        {/* Appointment Form */}
        {currentView === 'appointment' && !submitSuccess && (
          <div className="space-y-4">
            <button
              onClick={() => setCurrentView('home')}
              className="text-teal-600 text-sm flex items-center gap-1"
            >
              ← Back
            </button>
            
            <div className="space-y-3">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name *"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
                required
              />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Phone number *"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
                required
              />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email *"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
                required
              />
              
              <div className="flex gap-3">
                <button
                  onClick={() => setFormData({ ...formData, patient_type: 'new' })}
                  className={`flex-1 py-2 rounded-xl border ${formData.patient_type === 'new' ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200'}`}
                >
                  New Patient
                </button>
                <button
                  onClick={() => setFormData({ ...formData, patient_type: 'existing' })}
                  className={`flex-1 py-2 rounded-xl border ${formData.patient_type === 'existing' ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200'}`}
                >
                  Existing
                </button>
              </div>
              
              <select
                value={formData.preferred_day}
                onChange={(e) => setFormData({ ...formData, preferred_day: e.target.value })}
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Preferred day</option>
                <option value="Monday">Monday</option>
                <option value="Tuesday">Tuesday</option>
                <option value="Wednesday">Wednesday</option>
                <option value="Thursday">Thursday</option>
                <option value="Friday">Friday</option>
              </select>
              
              <select
                value={formData.preferred_time}
                onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Preferred time</option>
                <option value="Morning">Morning (8am-12pm)</option>
                <option value="Afternoon">Afternoon (12pm-5pm)</option>
              </select>
              
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Any additional notes..."
                className="w-full border border-gray-200 rounded-xl p-3 h-20 resize-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            
            <button
              onClick={handleSubmitAppointment}
              disabled={isSubmitting || !formData.name || !formData.phone || !formData.email}
              className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 disabled:bg-gray-300 transition"
            >
              {isSubmitting ? 'Submitting...' : 'Request Appointment'}
            </button>
          </div>
        )}

        {/* Messages/AI Chat View */}
        {currentView === 'messages' && (
          <div className="flex flex-col h-full">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto space-y-3 mb-4 min-h-[200px]">
              {messages.map((msg, index) => (
                <div
                  key={msg.id || index}
                  className={`flex ${msg.message_type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      msg.message_type === 'user'
                        ? 'bg-teal-600 text-white rounded-br-sm'
                        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                    <p className={`text-xs mt-1 ${msg.message_type === 'user' ? 'text-teal-200' : 'text-gray-400'}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              
              {/* AI Typing Indicator */}
              {isAiTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            {/* Chat Input */}
            <div className="border-t border-gray-200 pt-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={T('Ask us anything...', 'Pregúntanos lo que sea...')}
                  className="flex-1 border border-gray-200 rounded-full px-4 py-2 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  disabled={isAiTyping}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!chatInput.trim() || isAiTyping}
                  className="w-10 h-10 bg-teal-600 text-white rounded-full flex items-center justify-center hover:bg-teal-700 disabled:bg-gray-300 transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
              
              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => setChatInput("What services do you offer?")}
                  className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full hover:bg-gray-200"
                >
                  Services
                </button>
                <button
                  onClick={() => setChatInput("How much do dental implants cost?")}
                  className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full hover:bg-gray-200"
                >
                  Pricing
                </button>
                <button
                  onClick={() => setChatInput("Do you accept my insurance?")}
                  className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full hover:bg-gray-200"
                >
                  Insurance
                </button>
                <button
                  onClick={() => setCurrentView('appointment')}
                  className="text-xs bg-teal-100 text-teal-700 px-3 py-1 rounded-full hover:bg-teal-200"
                >
                  Book Appointment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help View */}
        {currentView === 'help' && (
          <div className="space-y-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help"
                className="w-full border border-gray-200 rounded-xl p-3 pl-10 focus:ring-2 focus:ring-teal-500"
              />
              <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            <button
              onClick={() => setCurrentView('forms')}
              className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between hover:shadow-md transition"
            >
              <span className="font-medium text-gray-800">Patient Information</span>
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <button
              onClick={() => setCurrentView('question')}
              className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between hover:shadow-md transition"
            >
              <span className="font-medium text-gray-800">Ask a Question</span>
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Patient Forms View */}
        {currentView === 'forms' && (
          <div className="space-y-4">
            <button
              onClick={() => setCurrentView('help')}
              className="text-teal-600 text-sm flex items-center gap-1"
            >
              ← Back
            </button>
            
            <h3 className="font-bold text-gray-800 text-lg">Patient Forms</h3>
            <p className="text-gray-500 text-sm">Save time by filling out our online forms, available for your convenience.</p>
            
            <div className="space-y-3">
              {[
                { name: 'Patient Information', url: '/form-finances#patient-info' },
                { name: 'Medical History', url: '/form-finances#medical-history' },
                { name: 'Notice of Privacy Practices', url: '/hipaa-npp' },
                { name: 'Dental History', url: '/form-finances#dental-history' },
                { name: 'Insurance Information', url: '/form-finances#insurance' }
              ].map((form, i) => (
                <a
                  key={i}
                  href={form.url}
                  className="block text-teal-600 hover:text-teal-700 hover:underline py-2"
                >
                  {form.name}
                </a>
              ))}
            </div>
            
            <div className="bg-gray-100 rounded-xl p-4 mt-6">
              <p className="text-gray-600 text-sm text-center">Did this answer your question?</p>
              <div className="flex justify-center gap-4 mt-3">
                {['😞', '😐', '😁'].map((emoji, i) => (
                  <button key={i} className="text-2xl hover:scale-125 transition">
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Question Form */}
        {currentView === 'question' && !submitSuccess && (
          <div className="space-y-4">
            <button
              onClick={() => setCurrentView('help')}
              className="text-teal-600 text-sm flex items-center gap-1"
            >
              ← Back
            </button>
            
            <select
              value={formData.topic}
              onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
              className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Select a topic</option>
              <option value="insurance">Insurance & Billing</option>
              <option value="services">Services & Procedures</option>
              <option value="hours">Hours & Location</option>
              <option value="appointment">Appointments</option>
              <option value="other">Other</option>
            </select>
            
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Type your question here..."
              className="w-full border border-gray-200 rounded-xl p-3 h-32 resize-none focus:ring-2 focus:ring-teal-500"
            />
            
            <div className="space-y-3">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email (for response)"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
              />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Phone"
                className="w-full border border-gray-200 rounded-xl p-3 focus:ring-2 focus:ring-teal-500"
              />
            </div>
            
            <button
              onClick={handleSubmitQuestion}
              disabled={isSubmitting || !formData.message.trim()}
              className="w-full bg-teal-600 text-white py-3 rounded-xl font-bold hover:bg-teal-700 disabled:bg-gray-300 transition"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Question'}
            </button>
          </div>
        )}

        {/* Success Message */}
        {submitSuccess && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Thank You!</h3>
            <p className="text-gray-600">Your submission has been received. We'll be in touch soon!</p>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="border-t border-gray-200 bg-white">
        <div className="flex">
          <button
            onClick={() => setCurrentView('home')}
            className={`flex-1 py-4 flex flex-col items-center gap-1 ${currentView === 'home' ? 'text-teal-600' : 'text-gray-500'}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-xs">Home</span>
          </button>
          <button
            onClick={() => setCurrentView('messages')}
            className={`flex-1 py-4 flex flex-col items-center gap-1 ${currentView === 'messages' ? 'text-teal-600' : 'text-gray-500'}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-xs">Messages</span>
          </button>
          <button
            onClick={() => setCurrentView('help')}
            className={`flex-1 py-4 flex flex-col items-center gap-1 ${currentView === 'help' || currentView === 'forms' || currentView === 'question' ? 'text-teal-600' : 'text-gray-500'}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs">Help</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;
